import { useEffect, useMemo, useState } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Pencil } from "lucide-react";

import { dataTableCustomStyles } from "../../config/dataTableTheme";
import { productModuleLabel } from "../../constants/productModules";
import type {
  AuthorizationBusinessUnit,
  AuthorizationBusinessUnitGroupMapping,
  AuthorizationExternalGroup,
  AuthorizationRole,
  AuthorizationRoleGroupMapping,
  BuScopeModuleMapping
} from "../../types/authorizationAdmin";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { withScrollableDataTable } from "./withScrollableDataTable";

const ScrollableDataTable = withScrollableDataTable(DataTable);

const DEFAULT_ROWS_PER_PAGE = 10;
/** Thead + pagination chrome for `fixedHeaderScrollHeight` (100% − offset). */
const EXTERNAL_GROUPS_TABLE_CHROME_OFFSET_PX = 132;

type ExternalGroupsTableProps = {
  readonly groups: AuthorizationExternalGroup[];
  readonly roles: readonly AuthorizationRole[];
  readonly businessUnits: readonly AuthorizationBusinessUnit[];
  readonly roleGroupMappings: readonly AuthorizationRoleGroupMapping[];
  readonly buGroupMappings: readonly AuthorizationBusinessUnitGroupMapping[];
  readonly buScopeModuleMappings: readonly BuScopeModuleMapping[];
  readonly onEdit: (group: AuthorizationExternalGroup) => void;
};

export const ExternalGroupsTable = ({
  groups,
  roles,
  businessUnits,
  roleGroupMappings,
  buGroupMappings,
  buScopeModuleMappings,
  onEdit
}: ExternalGroupsTableProps) => {
  const [searchText, setSearchText] = useState("");
  const [paginationReset, setPaginationReset] = useState(false);

  useEffect(() => {
    setPaginationReset((t) => !t);
  }, [searchText]);

  const roleById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const buById = useMemo(() => new Map(businessUnits.map((b) => [b.id, b])), [businessUnits]);

  type Row = AuthorizationExternalGroup & {
    readonly linksLabel: string;
    readonly modulesLabel: string;
    readonly actions: null;
  };

  const rows = useMemo<Row[]>(() => {
    return groups.map((g) => {
      let linksLabel = "—";
      let modulesLabel = "—";
      if (g.group_type === "ROLE") {
        const codes = roleGroupMappings
          .filter((m) => m.external_group_ref_id === g.id && m.is_active)
          .map((m) => roleById.get(m.decision_role_id)?.code ?? m.decision_role_id.slice(0, 8));
        linksLabel = codes.length ? codes.join(", ") : "—";
      } else {
        const codes = buGroupMappings
          .filter((m) => m.external_group_ref_id === g.id && m.is_active)
          .map((m) => buById.get(m.business_unit_id)?.code ?? m.business_unit_id.slice(0, 8));
        linksLabel = codes.length ? codes.join(", ") : "—";
        const modLabels = buScopeModuleMappings
          .filter((m) => m.external_group_ref_id === g.id && m.is_active)
          .map((m) => productModuleLabel(m.module_code));
        modulesLabel = modLabels.length ? modLabels.join(", ") : "—";
      }
      return { ...g, linksLabel, modulesLabel, actions: null };
    });
  }, [groups, roleGroupMappings, buGroupMappings, buScopeModuleMappings, roleById, buById]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(
      (r) =>
        r.group_name.toLowerCase().includes(q) || r.external_group_id.toLowerCase().includes(q)
    );
  }, [rows, searchText]);

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      { name: "Name", selector: (row) => row.group_name, grow: 2 },
      { name: "External ID", selector: (row) => row.external_group_id, grow: 2 },
      { name: "Type", selector: (row) => row.group_type, width: "110px" },
      {
        name: "Linked roles / BUs",
        selector: (row) => row.linksLabel,
        grow: 1,
        wrap: true
      },
      {
        name: "Linked modules",
        selector: (row) => row.modulesLabel,
        grow: 1,
        wrap: true
      },
      { name: "Provider", selector: (row) => row.provider, width: "100px" },
      { name: "Status", selector: (row) => (row.is_active ? "Active" : "Inactive"), width: "100px" },
      {
        name: "",
        width: "100px",
        cell: (row) => (
          <Button variant="ghost" className="!px-2 transition-all duration-200 ease-in-out" onClick={() => onEdit(row)} aria-label="Edit group">
            <Pencil className="h-4 w-4" />
          </Button>
        ),
        ignoreRowClick: true
      }
    ],
    [onEdit]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 space-y-1.5">
          <label htmlFor="external-groups-search" className="form-label">
            Search by name or external ID
          </label>
          <Input
            id="external-groups-search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Filter groups…"
            type="search"
            autoComplete="off"
          />
        </div>
      </div>

      {/* <div className="box-border flex h-[min(680px,58vh)] min-h-[440px] w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white pb-2"> */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ScrollableDataTable
            height="500px"
            chromeOffset={EXTERNAL_GROUPS_TABLE_CHROME_OFFSET_PX}
            columns={columns}
            data={filteredRows}
            customStyles={dataTableCustomStyles}
            keyField="id"
            pagination
            paginationPerPage={DEFAULT_ROWS_PER_PAGE}
            paginationRowsPerPageOptions={[10, 25, 50]}
            paginationResetDefaultPage={paginationReset}
            noDataComponent={
              <span className="py-6 text-sm text-gray-500">
                {searchText.trim()
                  ? "No groups match your search. Try another name or external ID."
                  : "No external groups yet. Add a group and link decision roles or business units in one step."}
              </span>
            }
          />
        </div>
      {/* </div> */}
    </div>
  );
};
